/**
 * Tracks whether the current document is being left, so a request the browser cancels on the way
 * out can be told apart from a real failure (TEAM-15511).
 *
 * Leaving the page mid-request rejects the in-flight fetch with the same `TypeError` a dropped
 * connection produces — Chrome's `"Failed to fetch (prod-coo-api.chowly.io)"`, Safari's
 * `"Load failed"` — and the rejection handler still runs. Verified on prod Chrome: at that moment
 * `beforeunload` had already fired, `pagehide` had not, and `document.visibilityState` was still
 * `visible`, so visibility alone cannot flag it.
 *
 * `beforeunload` is what catches that case. `pagehide` is listened to as well, for failures that
 * land later in the unload. It does NOT cover iOS: iOS Safari never fires `beforeunload`, and its
 * fetch rejects before `pagehide`, so at the moment the request fails the flag is still clear and
 * an iOS page exit reads as an ordinary network failure. The location sagas re-read the flag
 * after their same-origin probe (`redux/locations/saga.ts`, `reportLocationFetchFailure`), by which
 * time `pagehide` may have fired, so some iOS exits can still land in `page_unload`; that is not
 * guaranteed. A passive `beforeunload` listener does not block the back/forward cache.
 *
 * `pageshow` clears the flag: a page restored from the back/forward cache is live again. A
 * `beforeunload` the diner cancels (stays on the page) leaves the flag set until the next
 * `pageshow`; nothing on the ordering site prompts on unload, so that window is not reachable.
 *
 * Listeners are installed when this module is first imported, so the flag is set before any
 * request can fail, not lazily on the first read.
 */

let unloading = false;

const markUnloading = () => {
  unloading = true;
};

const markLive = () => {
  unloading = false;
};

function installPageLifecycleListeners() {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeunload', markUnloading);
  window.addEventListener('pagehide', markUnloading);
  window.addEventListener('pageshow', markLive);
}

/** True once the document has started unloading (and has not been restored since). */
export function isPageUnloading(): boolean {
  return unloading;
}

/** Test-only: clear the flag between cases. */
export function resetPageLifecycleForTests() {
  unloading = false;
}

installPageLifecycleListeners();
